/**
 * SMSF compliance maths (AU, FY 2025-26 and 2026-27): contribution caps with carry-forward and bring-forward, pension
 * minimums, exempt current pension income (ECPI), the trustee's annual checklist and a simple annual pack.
 * Rules and sources: docs/SMSF-COMPLIANCE.md. General information, not advice — the fund's accountant and auditor decide.
 */
import { z } from 'zod';
import { addDays, diffDays, mkDate } from '../dates.js';
import { allocateCents } from '../money.js';
import { AU_SUPER_CAPS, pensionMinimumDrawdownRate } from '../forecast/super.js';
import { discountFraction } from '../tax/au.js';
import { CentsSchema, IdSchema, ISODateSchema } from '../types.js';
// ───────────── persisted shapes (packages/db migration 0026) ─────────────
/** What a fund transaction (or part of one) is for one member. Concessional: employer, salary sacrifice, personal deductible. */
export const SmsfAllocationKindSchema = z.enum(['employer', 'salary_sacrifice', 'personal_deductible', 'non_concessional', 'pension', 'lump_sum']);
export const SMSF_CONCESSIONAL_KINDS = new Set(['employer', 'salary_sacrifice', 'personal_deductible']);
export const SmsfAllocationSchema = z.object({
    id: IdSchema, entityId: IdSchema, transactionId: IdSchema, memberId: IdSchema, kind: SmsfAllocationKindSchema,
    amountCents: CentsSchema.refine((n) => n > 0, 'amount must be positive'),
    createdAt: z.string(), updatedAt: z.string(),
});
/** Per member, per FY: balance at 1 July (or at commencement), total super balance at the prior 30 June, pension start. */
export const SmsfMemberYearSchema = z.object({
    openingBalanceCents: CentsSchema.nullable().prefault(null),
    tsbCents: CentsSchema.nullable().prefault(null),
    pensionCommencedOn: ISODateSchema.nullable().prefault(null),
});
export const SmsfFySettingsSchema = z.object({
    id: IdSchema, entityId: IdSchema, fy: z.string().regex(/^\d{4}-\d{2}$/),
    ecpiMethod: z.enum(['proportionate', 'segregated']).prefault('proportionate'),
    /** Actuarial certificate's exempt proportion, 0–100 (proportionate method). */
    actuarialExemptPct: z.number().min(0).max(100).nullable().prefault(null),
    /** Accounts holding segregated pension assets (segregated method): their income and gains are wholly exempt. */
    segregatedAccountIds: z.array(IdSchema).prefault([]),
    strategyReviewedOn: ISODateSchema.nullable().prefault(null),
    auditorName: z.string().max(120).prefault(''),
    auditorAppointedOn: ISODateSchema.nullable().prefault(null),
    auditPackSentOn: ISODateSchema.nullable().prefault(null),
    returnDueDate: ISODateSchema.nullable().prefault(null),
    returnLodgedOn: ISODateSchema.nullable().prefault(null),
    members: z.record(IdSchema, SmsfMemberYearSchema).prefault({}),
    createdAt: z.string(), updatedAt: z.string(),
});
/**
 * Caps by FY start year. 2025-26 is AU_SUPER_CAPS (the one source of truth for the current table); earlier years are
 * history for carry-forward and bring-forward. 2026-27: ATO "Key superannuation rates and thresholds" and the 2026-27
 * Budget summary — concessional $32,500, non-concessional $130,000 (bring-forward $390,000), transfer balance cap $2.1m.
 */
export const SMSF_CAPS = {
    2017: { concessionalCents: 2_500_000, nonConcessionalCents: 10_000_000, transferBalanceCapCents: 160_000_000 },
    2018: { concessionalCents: 2_500_000, nonConcessionalCents: 10_000_000, transferBalanceCapCents: 160_000_000 },
    2019: { concessionalCents: 2_500_000, nonConcessionalCents: 10_000_000, transferBalanceCapCents: 160_000_000 },
    2020: { concessionalCents: 2_500_000, nonConcessionalCents: 10_000_000, transferBalanceCapCents: 160_000_000 },
    2021: { concessionalCents: 2_750_000, nonConcessionalCents: 11_000_000, transferBalanceCapCents: 170_000_000 },
    2022: { concessionalCents: 2_750_000, nonConcessionalCents: 11_000_000, transferBalanceCapCents: 170_000_000 },
    2023: { concessionalCents: 2_750_000, nonConcessionalCents: 11_000_000, transferBalanceCapCents: 190_000_000 },
    2024: { concessionalCents: 3_000_000, nonConcessionalCents: 12_000_000, transferBalanceCapCents: 190_000_000 },
    2025: {
        concessionalCents: AU_SUPER_CAPS.concessionalCents, nonConcessionalCents: AU_SUPER_CAPS.nonConcessionalCents,
        transferBalanceCapCents: AU_SUPER_CAPS.transferBalanceCapCents,
    },
    2026: { concessionalCents: 3_250_000, nonConcessionalCents: 13_000_000, transferBalanceCapCents: 210_000_000 },
};
const LATEST_CAP_YEAR = Math.max(...Object.keys(SMSF_CAPS).map(Number));
/** Carry-forward of unused concessional cap: amounts from 2018-19 on, used while the TSB at the prior 30 June is under $500k. */
export const SMSF_CARRY_FORWARD_FIRST_YEAR = 2018;
export const SMSF_CARRY_FORWARD_TSB_LIMIT_CENTS = 50_000_000;
/** Bring-forward is only triggered by members under 75 at the start of the year. */
export const SMSF_BRING_FORWARD_MAX_AGE = 75;
/** Caps for an FY (by start year); years after the table use its latest row with `confirmed: false`. */
export const smsfCapsFor = (fyStartYear) => {
    const y = Math.min(Math.max(fyStartYear, 2017), LATEST_CAP_YEAR);
    return { ...SMSF_CAPS[y], confirmed: fyStartYear <= LATEST_CAP_YEAR };
};
const capStatus = (contributed, available) => contributed > available ? 'over' : available > 0 && contributed >= available * 0.9 ? 'near' : 'ok';
/**
 * One member's contributions for the FY against the caps. `history` holds earlier FYs by start year; a year missing
 * from it is unknown, so it adds no carry-forward and cannot have triggered a bring-forward. An unknown TSB gives no
 * carry-forward and the plain annual non-concessional cap (the conservative answer).
 * ponytail: past years are assumed to have been carry-forward eligible and any bring-forward triggered at 3×; enter a
 * smaller TSB-based figure via the fund's accountant if a past trigger was 2×.
 */
export const smsfContributionCaps = (p) => {
    const Y = p.fyStartYear;
    const caps = smsfCapsFor(Y);
    const known = (y) => p.history[y];
    // unused concessional cap, oldest first, each kept for five years
    const buckets = new Map();
    for (let y = SMSF_CARRY_FORWARD_FIRST_YEAR; y < Y; y++) {
        const h = known(y);
        if (!h)
            continue;
        const cap = smsfCapsFor(y).concessionalCents;
        let excess = Math.max(0, h.concessionalCents - cap);
        for (const [b, left] of [...buckets].sort((a, z) => a[0] - z[0])) {
            if (b < y - 5 || excess === 0)
                continue;
            const used = Math.min(left, excess);
            buckets.set(b, left - used);
            excess -= used;
        }
        buckets.set(y, Math.max(0, cap - h.concessionalCents));
    }
    const carryForwardEligible = p.tsbCents !== null && p.tsbCents < SMSF_CARRY_FORWARD_TSB_LIMIT_CENTS;
    const unused = [...buckets].filter(([b]) => b >= Y - 5).reduce((t, [, v]) => t + v, 0);
    const carryForwardCents = carryForwardEligible ? unused : 0;
    const ccAvailable = caps.concessionalCents + carryForwardCents;
    // non-concessional: an open bring-forward period from the last two years, else this year's TSB tier
    let periodStart = null;
    for (let y = Y - 4; y < Y; y++) {
        if (periodStart !== null && y <= periodStart + 2)
            continue;
        const h = known(y);
        if (h && h.nonConcessionalCents > smsfCapsFor(y).nonConcessionalCents)
            periodStart = y;
    }
    const annual = caps.nonConcessionalCents;
    const tbc = caps.transferBalanceCapCents;
    let ncAvailable;
    let years;
    if (p.tsbCents !== null && p.tsbCents >= tbc) {
        ncAvailable = 0;
        years = 0;
    }
    else if (periodStart !== null && Y <= periodStart + 2) {
        const used = Array.from({ length: Y - periodStart }, (_, i) => known(periodStart + i)?.nonConcessionalCents ?? 0).reduce((a, b) => a + b, 0);
        ncAvailable = Math.max(0, 3 * smsfCapsFor(periodStart).nonConcessionalCents - used);
        years = 3;
    }
    else if (p.tsbCents === null || p.ageAtFyStart >= SMSF_BRING_FORWARD_MAX_AGE) {
        ncAvailable = annual;
        years = 1;
    }
    else if (p.tsbCents < tbc - 2 * annual) {
        ncAvailable = 3 * annual;
        years = 3;
    }
    else if (p.tsbCents < tbc - annual) {
        ncAvailable = 2 * annual;
        years = 2;
    }
    else {
        ncAvailable = annual;
        years = 1;
    }
    return {
        capsConfirmed: caps.confirmed,
        concessional: {
            capCents: caps.concessionalCents, carryForwardCents, carryForwardEligible, availableCents: ccAvailable,
            contributedCents: p.concessionalCents, headroomCents: ccAvailable - p.concessionalCents, status: capStatus(p.concessionalCents, ccAvailable),
        },
        nonConcessional: {
            capCents: annual, availableCents: ncAvailable, contributedCents: p.nonConcessionalCents, headroomCents: ncAvailable - p.nonConcessionalCents,
            status: capStatus(p.nonConcessionalCents, ncAvailable),
            bringForward: { openPeriodFrom: periodStart !== null && Y <= periodStart + 2 ? periodStart : null, years },
        },
    };
};
const ageOn = (dob, on) => Number(on.slice(0, 4)) - Number(dob.slice(0, 4)) - (on.slice(5) < dob.slice(5) ? 1 : 0);
/**
 * Account-based (and transition-to-retirement) pension minimum for the FY: rate by age × balance at 1 July (or at
 * commencement), pro-rated by days when it starts mid-year, nil when it starts in June, rounded to the nearest $10
 * (SIS Regs Sch 7). TTR also has a 10% maximum.
 */
export const smsfPensionMinimum = (p) => {
    const { fy } = p;
    const commenced = p.commencedOn && p.commencedOn > fy.start ? p.commencedOn : null;
    const start = commenced ?? fy.start;
    const daysLeft = p.asOf > fy.end ? 0 : Math.max(0, diffDays(p.asOf, fy.end));
    const age = ageOn(p.dateOfBirth, start);
    const lastJune = mkDate(fy.endYear, 6, 1);
    const none = p.phase === 'accumulation' || (commenced !== null && (commenced >= lastJune || commenced > fy.end));
    const rate = none ? 0 : pensionMinimumDrawdownRate(age);
    const fyDays = diffDays(fy.start, fy.end) + 1;
    const days = diffDays(start, fy.end) + 1;
    const raw = (p.balanceCents * rate * days) / fyDays;
    const minimumCents = Math.round(raw / 1000) * 1000;
    const maximumCents = p.phase === 'ttr' ? Math.round(p.balanceCents * 0.1) : null;
    const elapsed = Math.min(days, Math.max(0, diffDays(start, p.asOf) + 1));
    const expectedToDate = Math.round((minimumCents * elapsed) / days);
    const status = none ? 'not_required'
        : p.paidCents >= minimumCents ? 'met'
            : p.asOf > fy.end ? 'missed'
                : p.paidCents < expectedToDate && daysLeft <= 90 ? 'at_risk' : 'on_track';
    return {
        required: !none, ageAtStart: age, rate, proRata: commenced !== null, startDate: start, minimumCents, maximumCents,
        paidCents: p.paidCents, remainingCents: Math.max(0, minimumCents - p.paidCents), overMaximum: maximumCents !== null && p.paidCents > maximumCents,
        daysLeft, status,
    };
};
// ───────────── ECPI and tax ─────────────
export const SMSF_TAX_RATE = 0.15;
/** 15% on a gain after the fund's ⅓ CGT discount (tax/au.ts discountFraction) = 10% of the gross gain. */
export const SMSF_DISCOUNTED_GAIN_RATE = discountFraction('smsf').neg().plus(1).mul(SMSF_TAX_RATE).toNumber();
/**
 * Exempt current pension income and the fund's income tax estimate. Proportionate: the actuarial certificate's % of
 * investment income and net capital gain is exempt, and the same share of expenses is not deductible. Segregated:
 * income and gains of the segregated pension accounts are wholly exempt and their expenses not deductible.
 * Assessable contributions are never exempt. `netCapitalGainCents` is after losses and the ⅓ discount (core CGT engine).
 * Taxable income is rounded down to whole dollars; franking credits are refundable.
 */
export const smsfEcpi = (p) => {
    const seg = p.method === 'segregated';
    const f = seg ? 0 : Math.min(100, Math.max(0, p.actuarialExemptPct ?? 0)) / 100;
    const exemptIncomeCents = seg
        ? (p.segregatedIncomeCents ?? 0) + (p.segregatedNetCapitalGainCents ?? 0)
        : Math.round((p.investmentIncomeCents + p.netCapitalGainCents) * f);
    const nonDeductibleCents = seg ? (p.segregatedExpensesCents ?? 0) : Math.round(p.expensesCents * f);
    const deductibleCents = p.expensesCents - nonDeductibleCents;
    const assessable = p.investmentIncomeCents + p.netCapitalGainCents + p.assessableContributionsCents - exemptIncomeCents;
    const taxableIncomeCents = Math.max(0, Math.floor((assessable - deductibleCents) / 100) * 100);
    const grossTaxCents = Math.round(taxableIncomeCents * SMSF_TAX_RATE);
    const frankingCreditsCents = p.frankingCreditsCents ?? 0;
    return {
        method: p.method, exemptPct: seg ? null : f * 100, needsCertificate: !seg && p.actuarialExemptPct === null,
        exemptIncomeCents, nonDeductibleCents, deductibleCents, taxableIncomeCents, grossTaxCents, frankingCreditsCents,
        taxPayableCents: grossTaxCents - frankingCreditsCents, discountedGainRate: SMSF_DISCOUNTED_GAIN_RATE,
    };
};
/** Annual return due date: the user's (tax agent lodgement program) or 31 October after the FY. */
export const smsfReturnDueDate = (fy, override) => override ?? mkDate(fy.endYear, 10, 31);
export const smsfChecklist = (p) => {
    const { fy, today, settings: s } = p;
    const returnDue = smsfReturnDueDate(fy, s.returnDueDate);
    const item = (key, label, done, dueDate, detail, issue = false) => ({ key, label, dueDate, detail, status: done ? 'done' : issue ? 'issue' : dueDate && today > dueDate ? 'overdue' : 'todo' });
    const pension = p.pensions.filter((x) => x.status !== 'not_required');
    const missed = pension.filter((x) => x.status === 'missed' || x.status === 'at_risk');
    const certNeeded = pension.length > 0 && p.hasAccumulationMembers && s.ecpiMethod === 'proportionate';
    const reviewedInYear = !!s.strategyReviewedOn && s.strategyReviewedOn >= addDays(fy.start, -1) && s.strategyReviewedOn <= returnDue;
    return [
        item('strategy', 'Investment strategy reviewed', reviewedInYear, fy.end, s.strategyReviewedOn ? `Last reviewed ${s.strategyReviewedOn}.` : 'Review and minute the strategy at least once a year (SIS Reg 4.09).'),
        item('caps', 'Contributions within caps', p.capsOver.length === 0, fy.end, p.capsOver.length ? `Over a cap: ${p.capsOver.join(', ')}.` : p.capsNear.length ? `Close to a cap: ${p.capsNear.join(', ')}.` : 'No member is over a cap.', p.capsOver.length > 0),
        pension.length === 0 ? { key: 'pensions', label: 'Pension minimums paid', status: 'na', dueDate: fy.end, detail: 'No member is drawing a pension this year.' }
            : item('pensions', 'Pension minimums paid', pension.every((x) => x.status === 'met'), fy.end, missed.length ? `Not yet paid: ${missed.map((x) => x.name).join(', ')}.` : 'Pay each minimum by 30 June.', pension.some((x) => x.status === 'missed')),
        certNeeded ? item('actuarial', 'Actuarial certificate obtained', s.actuarialExemptPct !== null, returnDue, s.actuarialExemptPct !== null ? `Exempt proportion ${s.actuarialExemptPct}%.` : 'Needed to claim ECPI under the proportionate method.')
            : { key: 'actuarial', label: 'Actuarial certificate obtained', status: 'na', dueDate: null, detail: 'Not needed: no pension members, or assets are segregated.' },
        item('auditor', 'Approved auditor appointed', !!s.auditorAppointedOn, addDays(returnDue, -45), s.auditorAppointedOn ? `${s.auditorName || 'Auditor'} appointed ${s.auditorAppointedOn}.` : 'Appoint at least 45 days before the return is due.'),
        item('auditPack', 'Audit pack sent', !!s.auditPackSentOn, addDays(returnDue, -14), s.auditPackSentOn ? `Sent ${s.auditPackSentOn}.` : 'Send the annual pack and source documents to the auditor.'),
        item('return', 'Annual return lodged', !!s.returnLodgedOn, returnDue, s.returnLodgedOn ? `Lodged ${s.returnLodgedOn}.` : `Due ${returnDue}.`),
    ];
};
/**
 * Operating statement, statement of financial position and member statements. Earnings (income − expenses) and the
 * fund's tax beyond 15% contributions tax are allocated pro rata to opening balances (to contributions when every
 * opening balance is nil). `unreconciledCents` = net assets − member benefits; non-zero means opening balances or
 * records are incomplete.
 * ponytail: opening-balance weighting, not daily-weighted; fine for a simple pack, the fund's accountant does the final.
 */
export const smsfAnnualPack = (p) => {
    const sum = (xs) => xs.reduce((t, x) => t + x.cents, 0);
    const incomeCents = sum(p.income);
    const expensesCents = sum(p.expenses);
    const ms = p.members;
    const contributionsCents = ms.reduce((t, m) => t + m.concessionalCents + m.nonConcessionalCents, 0);
    const benefitsCents = ms.reduce((t, m) => t + m.pensionsCents + m.lumpSumsCents, 0);
    const earningsCents = incomeCents - expensesCents;
    const contribTax = ms.map((m) => Math.round(m.concessionalCents * SMSF_TAX_RATE));
    const weights = ms.some((m) => m.openingCents > 0) ? ms.map((m) => m.openingCents) : ms.map((m) => m.concessionalCents + m.nonConcessionalCents);
    const earnings = ms.length ? allocateCents(earningsCents, weights) : [];
    const otherTax = ms.length ? allocateCents(p.taxCents - contribTax.reduce((a, b) => a + b, 0), weights) : [];
    const members = ms.map((m, i) => {
        const taxCents = contribTax[i] + otherTax[i];
        return {
            ...m, earningsCents: earnings[i], taxCents,
            closingCents: m.openingCents + m.concessionalCents + m.nonConcessionalCents + earnings[i] - m.pensionsCents - m.lumpSumsCents - taxCents,
        };
    });
    const investmentsCents = sum(p.investments);
    const totalAssetsCents = investmentsCents + p.cashCents + sum(p.otherAssets);
    const liabilitiesCents = sum(p.liabilities);
    const netAssetsCents = totalAssetsCents - liabilitiesCents;
    const memberBenefitsCents = members.reduce((t, m) => t + m.closingCents, 0);
    return {
        fy: p.fy.label, from: p.fy.start, to: p.fy.end,
        operating: {
            income: p.income, incomeCents, contributionsCents, expenses: p.expenses, expensesCents, benefitsCents,
            resultBeforeTaxCents: earningsCents, taxCents: p.taxCents,
            changeInNetAssetsCents: incomeCents + contributionsCents - expensesCents - benefitsCents - p.taxCents,
        },
        position: {
            investments: p.investments, investmentsCents, cashCents: p.cashCents, otherAssets: p.otherAssets, totalAssetsCents,
            liabilities: p.liabilities, liabilitiesCents, netAssetsCents, memberBenefitsCents, unreconciledCents: netAssetsCents - memberBenefitsCents,
        },
        members,
    };
};
// ───────────── allocation suggestions ─────────────
const CONTRIBUTION_HINT = /\b(super|sg|sgc|contrib\w*|clearing|employer|salary sacrifice|quicksuper|beam|ato)\b/i;
const PENSION_HINT = /\b(pension|drawdown|income stream)\b/i;
/**
 * Suggest a member and type for unallocated fund transactions: deposits that look like contributions (employer SG via a
 * clearing house, etc.) and payments that look like pensions, when exactly one member's first name is in the text.
 * Everything else is left for the trustee.
 */
export const suggestSmsfAllocations = (txns, members) => {
    const first = members.map((m) => ({ id: m.id, word: (m.name.trim().split(/\s+/)[0] ?? '').toLowerCase() })).filter((m) => m.word.length > 1);
    const unique = first.filter((m) => first.filter((o) => o.word === m.word).length === 1);
    return txns.flatMap((t) => {
        const text = `${t.payeeRaw} ${t.memo}`;
        const words = new Set(text.toLowerCase().split(/[^a-z']+/));
        const hits = unique.filter((m) => words.has(m.word));
        if (hits.length !== 1)
            return [];
        const kind = t.amountCents > 0 && CONTRIBUTION_HINT.test(text) ? 'employer'
            : t.amountCents < 0 && PENSION_HINT.test(text) ? 'pension' : null;
        return kind ? [{ transactionId: t.id, memberId: hits[0].id, kind, amountCents: Math.abs(t.amountCents) }] : [];
    });
};
//# sourceMappingURL=compliance.js.map